import React, { useEffect, useState } from "react"
import { Eye, EyeOff } from "lucide-react"
import { useLocation, globalHistory } from "@reach/router"
import * as styles from "./SaaSAuthModal.module.scss"
import { useSaaS } from "../../context/SaaSContext"
import ReCAPTCHA from "react-google-recaptcha"

declare global {
  interface Window {
    firebase?: any
  }
}

const FIREBASE_PROJECT_ID = process.env.GATSBY_FIREBASE_PROJECT_ID || ""
const FIREBASE_WEB_API_KEY = process.env.GATSBY_FIREBASE_WEB_API_KEY || ""
// const FIREBASE_AUTH_DOMAIN = FIREBASE_PROJECT_ID
//   ? `${FIREBASE_PROJECT_ID}.firebaseapp.com`
//   : ""
const FIREBASE_AUTH_DOMAIN = "digitup.com"

const loadScript = (id: string, src: string) =>
  new Promise<void>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Google sign-in is only available in the browser."))
      return
    }

    const existingScript = document.getElementById(id) as
      | HTMLScriptElement
      | null
    if (existingScript?.dataset.loaded === "true") {
      resolve()
      return
    }

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true })
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Unable to load Google sign-in.")),
        { once: true }
      )
      return
    }

    const script = document.createElement("script")
    script.id = id
    script.src = src
    script.async = true
    script.defer = true
    script.onload = () => {
      script.dataset.loaded = "true"
      resolve()
    }
    script.onerror = () => reject(new Error("Unable to load Google sign-in."))
    document.head.appendChild(script)
  })

const loadFirebaseAuth = async () => {
  await loadScript(
    "firebase-app-compat",
    "https://www.gstatic.com/firebasejs/10.12.5/firebase-app-compat.js"
  )
  await loadScript(
    "firebase-auth-compat",
    "https://www.gstatic.com/firebasejs/10.12.5/firebase-auth-compat.js"
  )

  if (!window.firebase?.apps?.length) {
    window.firebase.initializeApp({
      apiKey: FIREBASE_WEB_API_KEY,
      authDomain: FIREBASE_AUTH_DOMAIN,
      projectId: FIREBASE_PROJECT_ID,
    })
  }
}

const SaaSAuthModal = () => {
  const {
    authModalOpen,
    authMode,
    authLoading,
    closeAuthModal,
    openAuthModal,
    login,
    signup,
    loginWithGoogle,
    resetPassword,
    isAuthenticated,
  } = useSaaS()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [fullName, setFullName] = useState("")
  const [mobile, setMobile] = useState("")
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null)

  // Use Google's official test key for localhost and netlify previews so it doesn't crash
  const isDevelopment = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname.includes("netlify.app"))
  const recaptchaSiteKey = isDevelopment 
    ? "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" // Google Test Key (always passes)
    : "6Lff330pAAAAANMojwzHIijMv7L1S60UDvXqLk-R" // Your Production Key

  useEffect(() => {
    setEmail("")
    setPassword("")
    setShowPassword(false)
    setFullName("")
    setMobile("")
    setError("")
    setSuccessMessage("")
    setRecaptchaValue(null)
  }, [authModalOpen])

  useEffect(() => {
    setError("")
    setSuccessMessage("")
    setShowPassword(false)
    setRecaptchaValue(null)
  }, [authModalOpen, authMode])

  useEffect(() => {
    if (!authModalOpen || isAuthenticated) {
      return
    }

    let cancelled = false

    if (!FIREBASE_PROJECT_ID || !FIREBASE_WEB_API_KEY) {
      return
    }

    loadFirebaseAuth()
      .then(() => undefined)
      .catch(scriptError => {
        if (!cancelled) {
          setError(scriptError?.message || "Unable to load Google sign-in.")
        }
      })

    return () => {
      cancelled = true
    }
  }, [authModalOpen, isAuthenticated])

  const location = useLocation()

  useEffect(() => {
    if (typeof window === "undefined" || isAuthenticated) {
      return
    }

    const checkUrlForAuthModal = () => {
      const searchStr = location?.search || (typeof window !== "undefined" ? window.location.search : "")
      const params = new URLSearchParams(searchStr)
      const authParam = params.get("auth") || params.get("authMode")
      if (
        authParam === "login" ||
        authParam === "signup" ||
        authParam === "forgot_password" ||
        params.get("openAuth") === "true"
      ) {
        const mode =
          authParam === "signup" || authParam === "forgot_password"
            ? authParam
            : "login"
        openAuthModal(mode as any)

        const url = new URL(window.location.href)
        url.searchParams.delete("auth")
        url.searchParams.delete("authMode")
        url.searchParams.delete("openAuth")
        window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash)
      }
    }

    checkUrlForAuthModal()

    const unlisten = globalHistory.listen(() => {
      setTimeout(checkUrlForAuthModal, 50)
    })

    return () => {
      unlisten()
    }
  }, [location?.search, openAuthModal, isAuthenticated])

  if (!authModalOpen) {
    return null
  }

  const isSignup = authMode === "signup"
  const isForgotPassword = authMode === "forgot_password"

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccessMessage("")

    try {
      if (isForgotPassword) {
        await resetPassword(email)
        setSuccessMessage("If an account exists, a reset link has been sent to your email.")
        return
      } else if (isSignup) {
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&._-]).{8,}$/
        if (!passwordRegex.test(password)) {
          throw new Error("Password must be at least 8 characters long, and contain at least one uppercase letter, one lowercase letter, one number, and one special character.")
        }
        const result = await signup(email, password, fullName, mobile)
        if (result && result.success) {
          setSuccessMessage(result.message || "Account created! Please check your email for verification.")
          setPassword("")
          setFullName("")
          setMobile("")
          return
        }
      } else {
        await login(email, password)
      }

      setEmail("")
      setPassword("")
    } catch (submitError: any) {
      setError(submitError?.message || "Unable to continue.")
    }
  }

  const handleGoogleLogin = async () => {
    if (!FIREBASE_PROJECT_ID || !FIREBASE_WEB_API_KEY) {
      setError(
        "Google sign-in is not configured. Add the Firebase public config first."
      )
      return
    }

    try {
      setError("")
      await loadFirebaseAuth()

      const provider = new window.firebase.auth.GoogleAuthProvider()
      const result = await window.firebase.auth().signInWithPopup(provider)
      const firebaseIdToken = await result?.user?.getIdToken()

      if (!firebaseIdToken) {
        setError("Google did not return a sign-in token.")
        return
      }

      await loginWithGoogle(firebaseIdToken)
    } catch (submitError: any) {
      const code = submitError?.code || ""
      if (code.includes("popup-closed-by-user")) {
        setError("Google sign-in was cancelled.")
        return
      }

      setError(submitError?.message || "Unable to continue with Google.")
    }
  }

  return (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={() => {
        closeAuthModal()
      }}
    >
      <section
        className={styles.authModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="digitup-auth-title"
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close authentication modal"
          onClick={() => {
            closeAuthModal()
          }}
        >
          x
        </button>

        <p className={styles.modeLabel}>
          {isForgotPassword ? "Reset Password" : isSignup ? "Sign Up" : "Login"}
        </p>
        <h2 id="digitup-auth-title">
          {isForgotPassword
            ? "Forgot Password?"
            : isSignup
            ? "Create Your Account"
            : "Welcome Back"}
        </h2>
        <p className={styles.helperText}>
          {isForgotPassword
            ? "Enter your email to receive a password reset link."
            : "To get your complete report for Digitup.com (Please use your work email address to sign up and access a detailed enterprise-level report.)"}
        </p>

        {!isForgotPassword && (
          <>
            <div className={styles.googleWrapper}>
              <button
                type="button"
                className={styles.googleFallback}
                onClick={handleGoogleLogin}
                disabled={authLoading}
              >
                <span>G</span>
                {authLoading ? "Please wait..." : "Continue with Google"}
              </button>
            </div>

            <div className={styles.divider}>
              <span>or</span>
            </div>
          </>
        )}

        <form className={styles.authForm} onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Enter Your Email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            required
          />
          {isSignup && (
            <>
              <input
                type="text"
                placeholder="Full Name (Optional)"
                value={fullName}
                onChange={event => setFullName(event.target.value)}
              />
              <input
                type="tel"
                placeholder="Mobile Number (Optional)"
                value={mobile}
                onChange={event => setMobile(event.target.value)}
              />
            </>
          )}
          {!isForgotPassword && (
            <>
              <div className={styles.passwordInputWrapper}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  required
                  minLength={8}
                />
                {password.length > 0 && (
                  <button
                    type="button"
                    className={styles.eyeButton}
                    onClick={() => setShowPassword(prev => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              </div>
              {!isSignup && (
                <button
                  type="button"
                  className={styles.forgotPasswordLink}
                  onClick={() => openAuthModal("forgot_password")}
                >
                  Forgot Password?
                </button>
              )}
            </>
          )}

          {!isSignup && !isForgotPassword && (
            <div className={styles.captchaWrapper}>
              <ReCAPTCHA
                sitekey={recaptchaSiteKey}
                onChange={value => setRecaptchaValue(value)}
              />
            </div>
          )}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={
              authLoading || 
              (!isSignup && !isForgotPassword && !recaptchaValue)
            }
          >
            {authLoading
              ? "Please wait..."
              : isForgotPassword
              ? "Send Reset Link"
              : isSignup
              ? "Sign Up"
              : "Login"}
          </button>
        </form>

        {error && (
          <p className={styles.errorText} role="alert">
            {error}
          </p>
        )}

        {successMessage && (
          <p className={styles.successText} role="alert" style={{ textAlign: "center", color: "green", fontSize: "0.85rem", marginTop: "1rem" }}>
            {successMessage}
          </p>
        )}

        <button
          type="button"
          className={styles.switchButton}
          onClick={() => {
            setError("")
            setSuccessMessage("")
            if (isForgotPassword) {
              openAuthModal("login")
            } else {
              openAuthModal(isSignup ? "login" : "signup")
            }
          }}
        >
          {isForgotPassword
            ? "Back to Login"
            : isSignup
            ? "Already have an account? Login"
            : "Need an account? Sign up"}
        </button>
      </section>
    </div>
  )
}

export default SaaSAuthModal
